import React from 'react';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../../context/useAppContext';
import { Dropdown, Modal, Button, Form, Icon, RadioLabel, SelectLabel, ToastContext } from '@weezevent/nacre';
import RateModel from '../../../../models/rateModel';
import RateTemplate from '../../../../models/rateTemplate';
import PropTypes from 'prop-types';
import { requiredField, useForm } from '../../../../utils/validators';
import { RateNameInput } from '../inputs/RateInputs';
import { LANGUAGES } from '../../../../components/translations/translationModal';
import { initLanguage, handleTranslations } from '../../../event_creation/translationsUtils';

import Styles from './duplicateAction.module.css';

export function DuplicateModal({ selected, form, onChangeForm, onChangeFormObject, onSave, onClose, isSubmitting, formErrors, groups, event, defaultLanguage, languageOptions }) {
    const { t } = useTranslation();

    const mapToGroups = React.useCallback(group => ({ key: group.pk, text: group.name ?? group?.group_template?.name, value: group.pk }), []);
    const optionsGroups = React.useMemo(() => {
        let firstOption = [
            {
                key: `no_group`,
                text: t(`ticket.rates.infos.noGroups`),
                value: `no_group`
            }
        ];

        return firstOption.concat(groups.filter(group => group !== null).map(mapToGroups));
    }, [groups, mapToGroups]);

    const canBeSubmitted = React.useMemo(() => {
        return form?.name && !formErrors?.name;
    }, [form, formErrors]);

    if (!selected) return null;

    const initLanguageRate = React.useCallback((value, name) => initLanguage(value, name, form, onChangeFormObject), [form, onChangeFormObject]);

    const handleTranslationsRate = React.useCallback(
        (value, property, selectedLanguage) => handleTranslations(value, property, selectedLanguage, form, onChangeFormObject, defaultLanguage),
        [form, onChangeFormObject, defaultLanguage]
    );

    return (
        <Modal
            open={Boolean(selected)}
            onClose={onClose}
            allowExternalClosing={false}
            title={t(`ticket.rates.duplicateRateTitle`, { name: selected.name })}
            subtitle={t('ticket.rates.duplicateRateSubtitle')}
            submitButton={
                <Button primary disabled={!canBeSubmitted || isSubmitting} isLoading={isSubmitting} onClick={() => onSave(selected, form)} label={t(`ticket.common.action.save`)} />
            }
            cancelButton={<Button onClick={onClose} label={t(`ticket.common.action.cancel`)} />}
        >
            <Form>
                <Form.Field>
                    <RateNameInput
                        errors={formErrors['name'] ? true : false}
                        rate={selected}
                        form={form}
                        event={event}
                        defaultLanguage={defaultLanguage}
                        languageOptions={languageOptions}
                        onChangeForm={onChangeForm}
                        initLanguage={initLanguageRate}
                        onChangeFormObject={onChangeFormObject}
                        handleTranslations={handleTranslationsRate}
                        className={Styles['duplicate-action-name']}
                    />
                    <Form.FieldErrors errors={formErrors['name'] ? [t(formErrors['name'])] : null} />
                </Form.Field>
                <Form.Field style={{ marginBottom: '0' }}>
                    <Form.Label label={t(`ticket.rates.infos.disableLabel`)} />
                    <div>
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            name={'unable'}
                            checked={!form.disabled}
                            label={
                                <span>
                                    {t(`ticket.rates.infos.unable`)} <Icon name="eye" />
                                </span>
                            }
                            onClick={() => {
                                onChangeForm('disabled', false);
                            }}
                        />
                        <RadioLabel
                            className={Styles['radio-label-custom']}
                            inline
                            bordered
                            name={'disable'}
                            checked={form.disabled}
                            label={
                                <span>
                                    {t(`ticket.rates.infos.disable`)} <Icon name="eye-slash" />
                                </span>
                            }
                            onClick={() => {
                                onChangeForm('disabled', true);
                            }}
                        />
                    </div>
                </Form.Field>
                {groups.length ? (
                    <Form.Field>
                        <SelectLabel
                            value={form.group === null ? 'no_group' : form.group}
                            label={t(`ticket.rates.infos.group`)}
                            onChange={value => onChangeForm('group', value === 'no_group' ? null : value)}
                            options={optionsGroups}
                        />
                    </Form.Field>
                ) : null}
            </Form>
        </Modal>
    );
}

DuplicateModal.propTypes = {
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    selected: PropTypes.object.isRequired,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    isSubmitting: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
    onChangeForm: PropTypes.func.isRequired,
    formErrors: PropTypes.object,
    groups: PropTypes.array.isRequired
};

export function DuplicateActionModal({ selectedRates, rates, onSave, onError = () => {}, onClose, ...props }) {
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const toast = React.useContext(ToastContext);

    const languageOptions = React.useMemo(() => {
        let list = event ? event : organization;
        return list.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization, event]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = event ? event.default_language : languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return event ? defaultLocale : defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions, event]);

    const initialForm = React.useMemo(() => {
        const rate = selectedRates[0];
        return {
            name: rate.name,
            disabled: rate.disabled,
            group: rate.group,
            translations: {
                ...(rate.translations || {}),
                name: {
                    ...(rate?.translations?.name || {}),
                    [defaultLanguage]: rate.name
                }
            }
        };
    }, [selectedRates, defaultLanguage]);

    let [formState, onChangeForm, onChangeFormObject, formErrors, formValidate, setErrors] = useForm(initialForm, {
        name: requiredField
    });

    const duplicate = React.useCallback(
        (rate, form) => {
            if (!formValidate()) {
                setIsSubmitting(false);
            }
            setIsSubmitting(true);
            let newRate = new RateModel(rate);
            newRate.name = form.name;
            newRate.disabled = form.disabled;
            newRate.group = form.group;
            newRate.relays = rate.relays.map(r => r.id);
            newRate.is_passculture = false;
            newRate.translations['name'] = form.translations.name;
            rate.display_order = rates.filter(frate => frate.group === (rate.group || null)).length || 0;
            delete newRate.id;

            if (newRate instanceof RateModel) {
                //add key form_id , so that the backend does not delete the linked form
                newRate.form_id = rate.form;
            }

            newRate
                .save({ organizationId: organization.pk, eventId: event.id })
                .then(() => {
                    onSave();
                    toast.success(t(`ticket.rates.duplicateRateSuccess`));
                })
                .catch(e => {
                    onError();
                    toast.error(t(`ticket.rates.duplicateRateError`));
                })
                .finally(() => {
                    setIsSubmitting(false);
                    onClose();
                });
        },
        [event, organization, onSave, onError, onClose, rates]
    );

    const _onClose = React.useCallback(() => {
        setErrors({});
        onClose();
    }, [onClose]);

    return (
        <DuplicateModal
            onSave={(rate, form) => duplicate(rate, form)}
            selected={selectedRates.length === 1 ? selectedRates[0] : null}
            form={formState}
            onClose={_onClose}
            isSubmitting={isSubmitting}
            onChangeForm={onChangeForm}
            onChangeFormObject={onChangeFormObject}
            formErrors={formErrors}
            organization={organization}
            event={event}
            defaultLanguage={defaultLanguage}
            languageOptions={languageOptions}
            {...props}
        />
    );
}

DuplicateActionModal.propTypes = {
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onError: PropTypes.func,
    rates: PropTypes.array.isRequired,
    selectedRates: PropTypes.array.isRequired
};

export function DuplicateAction({ objects: selectedRates, rates, withModal = true, groups, onClick = () => {}, onSave = () => {}, onError = () => {} }) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = React.useState(false);

    if (selectedRates?.length !== 1 || selectedRates[0] instanceof RateTemplate) {
        return null;
    }

    return (
        <React.Fragment>
            {/* instead of passing showModal to the modal.show we unmount the component to reset the form value */}
            {withModal && showModal && (
                <DuplicateActionModal rates={rates} groups={groups} selectedRates={selectedRates} onSave={onSave} onError={onError} onClose={() => setShowModal(false)} />
            )}
            <Dropdown.Item
                disabled={Boolean(selectedRates[0].rate_template || selectedRates[0].distributor)}
                onClick={() => {
                    onClick();
                    setShowModal(true);
                }}
            >
                {t('ticket.rates.duplicateRate')}
            </Dropdown.Item>
        </React.Fragment>
    );
}
DuplicateAction.propTypes = {
    objects: PropTypes.array, //required
    onSave: PropTypes.func,
    onError: PropTypes.func,
    rates: PropTypes.array.isRequired,
    groups: PropTypes.array.isRequired
};
